/**
 * Agents Page — Enterprise design inspired by Chatbase
 *
 * Clean: "Agents" title + "+ New AI agent" button.
 * Cards with gradient preview thumbnails.
 * Plan limit banner when quota reached.
 */

import React, { useState } from 'react'
import { Plus, Sparkles } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { ChatbotGrid } from '../../components/chatbot/ChatbotGrid'
import { TestChatModal } from '../../components/chatbot/TestChatModal'
import { useChatbots, useChatbotStore } from '../../stores/chatbotStore'
import type { Chatbot } from '../../types'
import { getUserPlans } from '../../services/plansApi'
import { useToast } from '../../hooks/useToast'
import { useNavigate } from 'react-router-dom'

export const ChatbotsPage: React.FC = () => {
  const [isTestModalOpen, setIsTestModalOpen] = useState(false)
  const [selectedChatbot, setSelectedChatbot] = useState<Chatbot | null>(null)
  const [chatbotLimit, setChatbotLimit] = useState<number | null>(null)
  const { toast } = useToast()
  const navigate = useNavigate()

  const { chatbots } = useChatbots()

  React.useEffect(() => {
    let mounted = true
    const loadPlans = async () => {
      try {
        const res = await getUserPlans()
        const planId = res.current_subscription?.plan_id
        const planName = res.current_subscription?.plan_name
        const plans = Array.isArray(res.available_plans) ? res.available_plans : []
        const currentPlan =
          plans.find(p => (planId && p.id === planId) || (planName && p.name === planName)) ||
          plans.find(
            p =>
              p.name?.toLowerCase() === planName?.toLowerCase() ||
              p.display_name?.toLowerCase() === planName?.toLowerCase()
          )
        const limit = currentPlan?.chatbots_limit
        if (mounted) setChatbotLimit(typeof limit === 'number' ? limit : null)
      } catch {
        if (mounted) setChatbotLimit(null)
      }
    }
    loadPlans()
    return () => { mounted = false }
  }, [])

  const total = chatbots.length
  const limitReached = typeof chatbotLimit === 'number' && chatbotLimit >= 0 && total >= chatbotLimit

  const handleCreate = () => {
    if (limitReached) {
      toast({ title: 'Agent limit reached', description: 'Upgrade your plan to create more agents.', variant: 'warning', duration: 6000 })
      navigate('/dashboard/plans')
      return
    }
    navigate('/dashboard/agents/new')
  }

  const handleTest = async (chatbot: Chatbot) => {
    try {
      const store = useChatbotStore.getState()
      await store.fetchChatbot(chatbot.id)
      const full = useChatbotStore.getState().chatbots.find(c => c.id === chatbot.id) || chatbot
      setSelectedChatbot(full)
      setIsTestModalOpen(true)
    } catch {
      setSelectedChatbot(chatbot)
      setIsTestModalOpen(true)
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header — Chatbase style: title left, button right */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Agents</h1>
        <Button onClick={handleCreate} disabled={limitReached}>
          <Plus className="w-4 h-4 mr-1.5" />
          New AI agent
        </Button>
      </div>

      {/* Plan limit banner */}
      {limitReached && (
        <div className="flex items-center gap-3 rounded-lg border border-orange-400 bg-orange-50 px-4 py-3 dark:bg-orange-950/30 dark:border-orange-800">
          <Sparkles className="w-4 h-4 text-orange-600 dark:text-orange-400 shrink-0" />
          <p className="text-sm text-orange-800 dark:text-orange-200 flex-1">
            You've reached your plan limit of <span className="font-semibold">{chatbotLimit}</span> agents.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard/plans')}
            className="shrink-0"
          >
            Upgrade
          </Button>
        </div>
      )}

      {/* Agent grid */}
      <ChatbotGrid
        showControls={true}
        showActions={true}
        onDelete={() => {}}
        onTest={handleTest}
      />

      <TestChatModal
        isOpen={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        chatbot={selectedChatbot}
      />
    </div>
  )
}
